/**
 * Shared utility functions used by the visibility engine.
 * Pure functions with zero external dependencies.
 */

import type {
  DropDownNoSelection,
  DropDownResult,
  IdCaptionType,
} from './types';

/**
 * Force-asserts an unknown value to a given type.
 * Useful when the runtime shape is known but TypeScript can't infer it.
 */
export const forceAssert = <T>(item: unknown): T => {
  return item as T;
};

/**
 * Checks if a value is a non-null object.
 */
export function isObject(object: unknown): object is Record<string, unknown> {
  return typeof object === 'object' && object !== null;
}

/**
 * Checks if every element in an array is of a given primitive type.
 */
export function isArrayOf(array: unknown, typeName: string) {
  return (
    Array.isArray(array) &&
    array.length > 0 &&
    array.every((item) => typeof item === typeName)
  );
}

/**
 * Checks if an object has `id: number` and `caption: string` properties.
 */
export function isIdCaption(object: unknown): object is IdCaptionType {
  return (
    isObject(object) &&
    Object.prototype.hasOwnProperty.call(object, 'id') &&
    typeof object.id === 'number' &&
    Object.prototype.hasOwnProperty.call(object, 'caption') &&
    typeof object.caption === 'string'
  );
}

/**
 * Checks if an array contains only IdCaptionType items.
 */
export function isArrayOfIdCaption(
  object: unknown,
): object is Array<IdCaptionType> {
  return (
    Array.isArray(object) && object.length > 0 && object.every(isIdCaption)
  );
}

/**
 * Checks if an object looks like a DropDownResult (has `value` and `text: string`).
 */
export function isDropDownResult(object: unknown): object is DropDownResult {
  return (
    isObject(object) &&
    object['value'] !== undefined &&
    typeof object.text === 'string'
  );
}

/**
 * Checks if an array contains only DropDownResult items.
 */
export function isArrayDropDownResult(
  object: unknown,
): object is Array<DropDownResult> {
  return (
    Array.isArray(object) && object.length > 0 && object.every(isDropDownResult)
  );
}

/**
 * Checks if an object is a DropDownNoSelection.
 */
export function isDropDownNoSelection(
  object: unknown,
): object is DropDownNoSelection {
  return isObject(object) && object['noSelectionValue'] !== undefined;
}
